import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  FlatList,
  View,
  Text,
  Image,
  TouchableOpacity,
  Dimensions,
} from "react-native";
import { AppColors } from "../../../../../../utility/AppColors";
import { AppFontStyle } from "../../../../../../styles/AppFontStyle";
import { AppImages } from "../../../../../../utility/AppImages";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { ChatMessage } from "../../types";

interface ChatListProps {
  messages: ChatMessage[];
  isMobile: boolean;
}

const ChatList: React.FC<ChatListProps> = ({ messages, isMobile }) => {
  const deviceSize = useDeviceSize();
  const flatListRef = useRef<FlatList>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll when new messages arrive if at bottom
  useEffect(() => {
    if (!flatListRef.current || messages.length === 0 || !isAtBottom) return;

    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      try {
        flatListRef.current?.scrollToEnd({ animated: true });
      } catch (e) {
        // Ignore scroll errors
      }
    }, 100);

    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, [messages.length, isAtBottom]);

  const handleScroll = useCallback(
    (event: any) => {
      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent;
      const paddingToBottom = 20;
      const isBottom =
        layoutMeasurement.height + contentOffset.y >=
        contentSize.height - paddingToBottom;

      setIsAtBottom(isBottom);
      setShowScrollToBottom(!isBottom && messages.length > 0);
    },
    [messages.length]
  );

  const scrollToBottom = () => {
    flatListRef.current?.scrollToEnd({ animated: true });
    setShowScrollToBottom(false);
  };

  return (
    <View style={{ flex: 1, position: "relative" }}>
      {/* Header for Desktop */}
      {!isMobile && (
        <View
          style={{
            borderBottomWidth: 1,
            borderBottomColor: "#374151",
            paddingBottom: 8,
            marginBottom: 8,
          }}
        >
          <Text
            style={[
              deviceSize === DEVICE_SIZES.XL
                ? AppFontStyle.BOLD_ANYBODY_18
                : AppFontStyle.BOLD_ANYBODY_14,
              { marginVertical: 10, color: "white" },
            ]}
          >
            Stream Chat
          </Text>
        </View>
      )}

      <FlatList
        ref={flatListRef}
        data={messages}
        keyExtractor={(item) => item.id}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingVertical: 8 }}
        renderItem={({ item }) => (
          <View
            style={{
              flexDirection: "row",
              marginVertical: 8,
              columnGap: 10,
            }}
          >
            <Image
              source={item.avatar ? { uri: item.avatar } : AppImages.Ellipse1}
              style={{
                width: 27,
                height: 27,
                borderRadius: 100,
              }}
            />
            <View style={{ flex: 1, flexDirection: "row", columnGap: 5 }}>
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.XL
                    ? AppFontStyle.REGULAR_ANYBODY_16
                    : AppFontStyle.REGULAR_ANYBODY_14,
                  { color: "white" },
                ]}
              >
                {item.username}:
              </Text>
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_12,
                  { opacity: 0.5, lineHeight: 17, color: "white", flex: 1 },
                ]}
              >
                {item.message}
              </Text>
            </View>
            {/* If message has gift image, handled here if we add that to ChatMessage type later */}
          </View>
        )}
      />

      {/* Scroll to Bottom Button */}
      {showScrollToBottom && (
        <TouchableOpacity
          onPress={scrollToBottom}
          style={{
            position: "absolute",
            bottom: 20,
            right: 10,
            backgroundColor: "rgba(220, 38, 38, 0.9)",
            borderRadius: 20,
            width: 40,
            height: 40,
            justifyContent: "center",
            alignItems: "center",
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.3,
            shadowRadius: 4,
            elevation: 5,
            zIndex: 1000,
          }}
        >
          <Text style={{ color: "white", fontSize: 20, fontWeight: "bold" }}>
            ↓
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

export default ChatList;
